import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api.js';
import PageHeader from '../../components/PageHeader.jsx';
import Pagination from '../../components/Pagination.jsx';
import { Icon, SECTION_COLOR } from '../../icons.jsx';
import { TripAssignmentEditor, useTripAssignmentLookups } from './tripAssignmentShared.jsx';
import './Trips.css';

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3a2.85 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

// Same two helpers as Orders.jsx (client/src/modules/orders/Orders.jsx),
// duplicated rather than shared since neither file exports them — keeps
// the customer avatar badge identical between the two list pages.
function customerInitials(name) {
  if (!name) return '—';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
const AVATAR_COLORS = [
  '#4C8FE0', '#3FBF6F', '#F2A93B', '#E0475E', '#9B84E8', '#2DC4C4',
  '#E86E96', '#C57709', '#5B8C5A', '#B5548A', '#3670B3', '#D9822B',
  '#6B7280', '#0E9F6E', '#7E3AF2', '#D63384',
];
function customerColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

// Trip label mirrors CreateLoad's own trip-group-header convention
// (customerOrderNumber-01, -02...) so the same trip reads the same way
// whether you're looking at it from the order or from this list.
function tripLabel(t) {
  return t.customer_order_number
    ? `${t.customer_order_number}-${String(t.trip_number).padStart(2, '0')}`
    : `Trip ${t.trip_number}`;
}

function firstPickup(stops) {
  return stops.find(s => s.stop_type === 'pickup') || stops[0] || null;
}
function lastDelivery(stops) {
  for (let i = stops.length - 1; i >= 0; i--) {
    if (stops[i].stop_type === 'delivery') return stops[i];
  }
  return stops[stops.length - 1] || null;
}
function stopLocationLabel(s) {
  if (!s) return '—';
  return s.location_name || s.location || '—';
}
function stopDate(s) {
  if (!s || !s.scheduled_at) return '';
  return new Date(s.scheduled_at).toLocaleDateString();
}
function stopTime(s) {
  return s && s.stop_time ? String(s.stop_time).slice(0, 5) : '';
}

const TABS = [
  { key: 'assigned',   label: 'Assigned' },
  { key: 'unassigned', label: 'Unassigned' },
];

export default function Trips() {
  const [tab, setTab] = useState('assigned');
  const [trips, setTrips] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [expandedOrders, setExpandedOrders] = useState(() => new Set());
  const [expandedTrips, setExpandedTrips] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Holds only the id, not a snapshot of the trip — the panel always reads
  // the CURRENT trip from `trips` below, same as the inline expand-row
  // editor already does. That matters because patchTripAssignment reloads
  // `trips` after every change; if the panel held its own stale copy, a
  // second edit in the same panel session (e.g. switch type, then also
  // pick a driver) would patch from pre-edit values instead of what was
  // just saved a moment ago.
  const [editingTripId, setEditingTripId] = useState(null);
  // Assignment saves previously had no error handling at all — if the
  // PATCH failed (e.g. the trip_assignments columns this depends on,
  // migration_046/047, haven't been run on this database yet), the
  // dropdown just silently did nothing with no indication why, which
  // is indistinguishable from the whole feature being broken. Surfaced
  // here instead of swallowed.
  const [assignError, setAssignError] = useState(null);

  const lookups = useTripAssignmentLookups();

  const load = () => api(`/dispatch/trips?status=${tab}`).then(setTrips).catch(e => setError(e.message));
  useEffect(() => {
    setTrips(null);
    setError(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Full overwrite, not a partial merge — matches PATCH /trips/:id, which
  // always sets every assignment field from what's sent (so switching type
  // away from 'company' actually clears driver/truck/trailer server-side
  // too). Reloads the list afterward since a trip's status (and therefore
  // which tab it belongs on) can change as a result.
  const patchTripAssignment = async (trip, patch) => {
    setAssignError(null);
    const next = {
      assignmentType: trip.assignment_type || 'company',
      driverId: trip.driver_id || null, truckId: trip.truck_id || null, trailerId: trip.trailer_id || null,
      ownerOperatorId: trip.owner_operator_id || null, carrierId: trip.carrier_id || null,
      ...patch,
    };
    try {
      await api(`/dispatch/trips/${trip.trip_id}`, { method: 'PATCH', body: JSON.stringify(next) });
      load();
    } catch (e) {
      setAssignError(e.message);
    }
  };

  // Changing assignment type/status can move a trip to the OTHER tab
  // (Unassigned -> Assigned), which drops it out of `trips` here on the
  // next load(). If that happens while its edit panel is open, close it
  // instead of rendering against an undefined trip.
  const editingTrip = trips ? trips.find(t => t.trip_id === editingTripId) : null;
  useEffect(() => {
    if (editingTripId && trips && !editingTrip) setEditingTripId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trips]);

  const filtered = useMemo(() => {
    if (!trips) return [];
    const q = search.trim().toLowerCase();
    if (!q) return trips;
    return trips.filter(t => {
      const haystack = [
        tripLabel(t), t.load_number, t.customer_order_number, t.customer_name,
        stopLocationLabel(firstPickup(t.stops)), stopLocationLabel(lastDelivery(t.stops)),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [trips, search]);

  // Group matching trips by their parent order — one row per order, its
  // trips nested underneath when expanded, instead of a flat trip-per-row
  // table. Keeps the trip's own order in the array (order of first
  // appearance) rather than re-sorting.
  const orderGroups = useMemo(() => {
    const byLoad = new Map();
    for (const t of filtered) {
      if (!byLoad.has(t.load_id)) {
        byLoad.set(t.load_id, {
          loadId: t.load_id, loadNumber: t.load_number,
          customerOrderNumber: t.customer_order_number, customerName: t.customer_name,
          trips: [],
        });
      }
      byLoad.get(t.load_id).trips.push(t);
    }
    return [...byLoad.values()];
  }, [filtered]);

  // Switching tabs or searching invalidates whatever page you were on.
  useEffect(() => { setPage(1); }, [tab, search]);

  // Paginates by ORDER now, not by trip — each page shows N orders with
  // all of their matching trips nested underneath.
  const totalPages = Math.max(1, Math.ceil(orderGroups.length / pageSize));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  const pageGroups = orderGroups.slice((page - 1) * pageSize, page * pageSize);

  const toggleOrder = (loadId) => setExpandedOrders(prev => {
    const next = new Set(prev);
    if (next.has(loadId)) next.delete(loadId); else next.add(loadId);
    return next;
  });
  const toggleTrip = (tripId) => setExpandedTrips(prev => {
    const next = new Set(prev);
    if (next.has(tripId)) next.delete(tripId); else next.add(tripId);
    return next;
  });

  return (
    <div>
      <PageHeader icon={Icon.trips} title="Trips" subtitle="Trips still needing to be created and assigned" color={SECTION_COLOR.trips} />

      <div className="billing-stage-tabs">
        {TABS.map(t => (
          <button key={t.key} type="button"
            className={'billing-stage-tab' + (tab === t.key ? ' active' : '')}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="panel filter-row-panel">
        <div className="filter-row">
          <div className="icon-select trips-search">
            <span className="icon-select-icon"><Icon.search /></span>
            <input placeholder="Search by order #, customer, or location"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      {error && <div className="empty">Could not load trips — log in first ({error})</div>}
      {trips === null && !error && <div className="empty">Loading...</div>}
      {trips && trips.length === 0 && (
        <div className="empty">
          {tab === 'unassigned' ? 'No unassigned trips — everything’s been created.' : 'No assigned trips yet.'}
        </div>
      )}
      {trips && trips.length > 0 && orderGroups.length === 0 && <div className="empty">No trips match your search.</div>}

      {trips && pageGroups.length > 0 && (
        <div className="orders-table-wrap">
          <table className="orders-table trips-grouped-table">
            <colgroup>
              <col style={{ width: 40 }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '10%' }} />
            </colgroup>
            <thead>
              <tr>
                <th></th>
                <th>Trip # / Order #</th>
                <th>Customer</th>
                <th>Pickup</th>
                <th>Delivery</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {pageGroups.map(group => {
                const isOrderOpen = expandedOrders.has(group.loadId);
                const assignedCount = group.trips.filter(t => (t.status || 'unassigned') !== 'unassigned').length;
                return (
                  <React.Fragment key={group.loadId}>
                    <tr className="trips-order-row" onClick={() => toggleOrder(group.loadId)}>
                      <td>
                        <span className={'trip-row-chevron' + (isOrderOpen ? ' open' : '')}>▾</span>
                      </td>
                      <td colSpan={2}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="company-initial-badge" style={{ background: customerColor(group.customerName) }} title={group.customerName || ''}>
                            {customerInitials(group.customerName)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, color: 'var(--cl-blue, #4C8FE0)' }}>{group.customerOrderNumber || group.loadNumber}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-dim)', opacity: .75, marginTop: 2 }}>{group.customerName || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td colSpan={2} style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                        {group.trips.length} trip{group.trips.length === 1 ? '' : 's'}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--text-dim)' }}>{assignedCount}/{group.trips.length} assigned</td>
                    </tr>

                    {isOrderOpen && group.trips.map(t => {
                      const isTripOpen = expandedTrips.has(t.trip_id);
                      const pickup = firstPickup(t.stops);
                      const delivery = lastDelivery(t.stops);
                      return (
                        <React.Fragment key={t.trip_id}>
                          <tr className="trips-row trips-nested-row" onClick={() => toggleTrip(t.trip_id)}>
                            <td></td>
                            <td>
                              <div className="trips-nested-label">
                                <span className={'trip-row-chevron' + (isTripOpen ? ' open' : '')}>▾</span>
                                {tripLabel(t)}
                              </div>
                            </td>
                            <td></td>
                            <td>
                              {pickup ? (
                                <>
                                  <div>{stopLocationLabel(pickup)}</div>
                                  <div style={{ fontSize: 12, color: 'var(--red)' }}>{stopDate(pickup)} {stopTime(pickup)}</div>
                                </>
                              ) : '—'}
                            </td>
                            <td>
                              {delivery ? (
                                <>
                                  <div>{stopLocationLabel(delivery)}</div>
                                  <div style={{ fontSize: 12, color: 'var(--red)' }}>{stopDate(delivery)} {stopTime(delivery)}</div>
                                </>
                              ) : '—'}
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span className={`badge ${t.status || 'unassigned'}`}>{(t.status || 'unassigned').replace('_', ' ')}</span>
                                <button type="button" className="trip-edit-btn" title="Edit assignment"
                                  onClick={e => { e.stopPropagation(); setAssignError(null); setEditingTripId(t.trip_id); }}>
                                  <PencilIcon />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {isTripOpen && (
                            <tr className="trips-detail-row">
                              <td colSpan={6}>
                                <div className="trip-detail-stops">
                                  {t.stops.length === 0 && <div className="empty">No stops on this trip.</div>}
                                  {t.stops.map((s, i) => (
                                    <div key={i} className="trip-detail-stop-row">
                                      <span className={'stop-type-badge ' + (s.stop_type === 'pickup' ? 'pickup' : 'delivery')}>
                                        {s.stop_type === 'pickup' ? 'P' : 'D'}
                                      </span>
                                      <span className="trip-detail-stop-location">
                                        {stopLocationLabel(s)}{s.city ? `, ${s.city}` : ''}{s.province ? `, ${s.province}` : ''}
                                      </span>
                                      <span className="trip-detail-stop-time">{stopDate(s)} {stopTime(s)}</span>
                                    </div>
                                  ))}
                                </div>
                                <div className="trip-detail-assignment" onClick={e => e.stopPropagation()}>
                                  {assignError && isTripOpen && <div className="trip-assign-error">{assignError}</div>}
                                  <TripAssignmentEditor
                                    assignmentType={t.assignment_type}
                                    driverId={t.driver_id} truckId={t.truck_id} trailerId={t.trailer_id}
                                    ownerOperatorId={t.owner_operator_id} carrierId={t.carrier_id}
                                    lookups={lookups}
                                    onChange={patch => patchTripAssignment(t, patch)}
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={orderGroups.length}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      {editingTrip && createPortal(
        <>
          <div className="trip-edit-panel-backdrop" onClick={() => setEditingTripId(null)} />
          <div className="trip-edit-panel">
            <div className="trip-edit-panel-header">
              <div>
                <div className="trip-edit-panel-title">{tripLabel(editingTrip)}</div>
                <div className="trip-edit-panel-sub">{editingTrip.customer_name || editingTrip.customer_order_number || editingTrip.load_number}</div>
              </div>
              <button type="button" className="modal-close" onClick={() => setEditingTripId(null)}>✕</button>
            </div>
            <div className="trip-edit-panel-body">
              <div className="trip-detail-stops trip-edit-panel-stops">
                {editingTrip.stops.length === 0 && <div className="empty">No stops on this trip.</div>}
                {editingTrip.stops.map((s, i) => (
                  <div key={i} className="trip-detail-stop-row">
                    <span className={'stop-type-badge ' + (s.stop_type === 'pickup' ? 'pickup' : 'delivery')}>
                      {s.stop_type === 'pickup' ? 'P' : 'D'}
                    </span>
                    <span className="trip-detail-stop-location">
                      {stopLocationLabel(s)}{s.city ? `, ${s.city}` : ''}{s.province ? `, ${s.province}` : ''}
                    </span>
                    <span className="trip-detail-stop-time">{stopDate(s)} {stopTime(s)}</span>
                  </div>
                ))}
              </div>
              {assignError && <div className="trip-assign-error">{assignError}</div>}
              <TripAssignmentEditor
                assignmentType={editingTrip.assignment_type}
                driverId={editingTrip.driver_id} truckId={editingTrip.truck_id} trailerId={editingTrip.trailer_id}
                ownerOperatorId={editingTrip.owner_operator_id} carrierId={editingTrip.carrier_id}
                lookups={lookups}
                onChange={patch => patchTripAssignment(editingTrip, patch)}
              />
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
